import { useEffect, useState } from 'react';
import { Avatar } from '@/shared/components/Avatar';
import { Button } from '@/shared/components/Button';
import { FileUpload } from '@/shared/components/FileUpload';
import { cn } from '@/shared/helpers/cn';
import styles from './PhotoUploader.module.scss';

export interface PhotoUploaderProps {
  /** Nome do titular, usado nas iniciais quando nao ha foto. */
  name: string;
  /** URL da foto ja gravada. */
  currentUrl?: string | null;
  onSelect: (file: File | null) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Foto de perfil do paciente.
 *
 * **Sem captura por camera** — decisao de escopo da POC. O sistema legado oferece
 * "Ativar Câmera" ao lado de "Carregar Imagem"; aqui existe apenas o upload.
 *
 * A previa usa `URL.createObjectURL` e revoga o objeto ao trocar de arquivo, para
 * nao vazar memoria num formulario que o usuario edita varias vezes.
 */
export function PhotoUploader({ name, currentUrl, onSelect, disabled, className }: PhotoUploaderProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }

    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const choose = (selected: File): void => {
    setFile(selected);
    onSelect(selected);
  };

  const clear = (): void => {
    setFile(null);
    onSelect(null);
  };

  const shown = preview ?? currentUrl ?? null;

  /*
    Foto, rótulo, formato aceito e a ação de remover moram na **mesma caixa**, e
    é a caixa inteira que abre o seletor de arquivos. Antes eram duas: a prévia
    de um lado e a área de envio do outro, com um "Escolher arquivo" repetindo o
    que a área já fazia.
  */
  return (
    <FileUpload
      className={cn(styles.root, className)}
      label="Foto do paciente"
      hint="JPG, PNG ou WebP, até 5 MB."
      onSelect={choose}
      disabled={disabled}
      compact
      preview={<Avatar name={name} src={shown} size="lg" />}
      action={
        shown ? (
          <Button variant="tertiary" size="sm" onClick={clear} disabled={disabled}>
            Remover foto
          </Button>
        ) : null
      }
    />
  );
}
